/** @jsxImportSource @emotion/react */
import {
  ChangeEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLoading } from 'components/process/Loading';
import PropTypes from 'prop-types';
import MapChart, { PolygonData, SiteImage } from 'components/mapchart/MapChart';
import {
  findSetupOnSiteImage,
  findSetupPrjSummExcpList,
  findSetupPrjSummTrackRecord,
} from 'apis/setup/home/SetupProjectSummary';
import { SetupProjectSummary } from 'models/setup/home/SetupProjectSummary';
import { SetupMapChart } from 'models/setup/SetupMapChart';
import { findSetupMapChart, findSetupMapChartList } from 'apis/setup/SetupMapChart';
import CustomGrid from 'components/grids/CustomGrid';
import { FOBContainer, FactoryContainer } from 'components/home/setupLayout';
import { SummaryItem } from 'components/home/SummaryItem';
import {
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Checkbox,
  Button,
} from '@mui/material';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { tabs } from 'components/layouts/Tab';
import { tb } from 'components/layouts/Table';
import _ from 'lodash';
import { Code } from 'models/common/CommonCode';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { useMessageBar } from 'hooks/useMessageBar';
import { useLocation, useNavigate } from 'react-router-dom';
import { st } from 'components/inputs/CustomInput';
import { ContainerLayout } from 'components/layouts/ContainerLayout';
import { ItemList, SetupExcpParam } from 'models/setup/excp/excp';
import { getSetupPackingList } from 'apis/setup/excp/packingList';
import { CommonText } from 'ui/theme/Color';
import SetupEquipmentScheduleChartPopup from 'pages/setup/popup/SetupEquipmentScheduleChartPopup';
import { findScheduleStatus } from 'apis/setup/SetupSchedule';
import { IconButton } from 'components/buttons/IconSVG';
import { CommonUtil } from 'utils/CommonUtil';
import GridSettingButtons from 'components/grids/GridSettingButtons';
import DOMPurify from 'dompurify';

/**
 * @desc 프로젝트요약 상세 > FOB, 공장 Layout상세
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.02.09   jihwan89       공장레이아웃상세, POB 프로젝트 명 추가
 */

interface PageLocationState {
  elmPrjId?: string;
  elmPrjNm?: string;
  currTab?: number;
}
interface CustomCode extends Code {
  selected?: boolean;
}

const SetupProjectSummaryPage = () => {
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const location = useLocation();
  const state = { ...location.state };
  const navigate = useNavigate();

  const trackGridRef = useRef<any>();
  const fobGridRef = useRef<any>();
  const fobGridUiRef = useRef<any>();

  const [locationState, setLocationState] = useState<PageLocationState>(useLocation().state);
  const [statList, setStatList] = useState<CustomCode[]>([]);
  const [mapChart, setMapChart] = useState<SetupMapChart>({});
  const [mapChartList, setMapChartList] = useState<SetupMapChart[]>([]);
  const [recordList, setTrackRecordList] = useState<SetupProjectSummary[]>([]);
  const [excpList, setExcpList] = useState<SetupProjectSummary[]>([]);
  const [packingList, setPackingList] = useState<ItemList[]>([]); //그리드
  const [currTab, setCurrTab] = useState(locationState?.currTab || 0);
  const [onSiteImgList, setOnSiteImgList] = useState<SiteImage[]>([]);

  const [isOpenScheduleChart, setOpenScheduleChart] = useState<boolean>(false);
  const [scheduleChartCondition, setScheduleChartCondition] = useState<any>();

  const trackRecordLayout = useMemo(() => {
    return [
      {
        binding: 'prdnProcNm',
        visible: true,
        header: String(t('setup.label.공정', '공정')),
        align: 'center',
        width: 100,
      },
      {
        binding: 'eqpNm',
        visible: true,
        header: String(t('setup.label.설비', '설비')),
        align: 'center',
        width: '*',
        cellTemplate: CellMaker.makeLink({
          click: (e, ctx) => {
            if (locationState.elmPrjId && ctx.item?.tgtEqpId) {
              findScheduleStatus(locationState.elmPrjId, '', ctx.item.tgtEqpId).then((res) => {
                if (res?.length > 0) {
                  setOpenScheduleChart(true);
                  setScheduleChartCondition({
                    type: 'CNN',
                    elmPrjId: locationState.elmPrjId,
                    tgtEqpId: ctx.item.tgtEqpId,
                    targetData: res[0],
                  });
                } else {
                  openMessageBar({
                    type: 'warning',
                    content: t('setup.label.해당 데이터가 없습니다.', '해당 데이터가 없습니다.'),
                  });
                }
              });
            }
          },
        }),
      },
      {
        binding: 'prjSpNm',
        visible: true,
        header: String(t('setup.label.Step', 'Step')),
        align: 'center',
        width: 180,
      },
      {
        binding: 'planEndDt',
        visible: true,
        header: String(t('setup.label.계획완료일', '계획완료일')),
        align: 'center',
        width: 130,
      },
      {
        binding: 'statNm',
        visible: true,
        header: String(t('setup.label.상태', '상태')),
        align: 'center',
        width: 80,
      },
      {
        binding: 'atulEndDt',
        visible: true,
        header: String(t('setup.label.완료일', '완료일')),
        align: 'center',
        width: 130,
      },
      {
        binding: 'progRt',
        visible: true,
        header: String(t('setup.label.진행률(%)', '진행률(%)')),
        align: 'center',
        width: 100,
      },
    ];
  }, [recordList]);

  const fobEqpGrLayout = useMemo(() => {
    return [
      {
        binding: 'eqclId',
        header: String(t('com.label.설비군', '설비군')),
        width: 150,
        align: 'center',
      },
      {
        binding: 'eqpId',
        header: String(t('com.label.M-CODE', 'M-CODE')),
        width: 150,
        align: 'center',
        cellTemplate: CellMaker.makeLink({
          click: (e, ctx) => {
            onClickFob(ctx.item);
          },
        }),
      },
      {
        binding: 'eqpNm',
        header: String(t('com.label.설비명', '설비명')),
        width: 150,
        align: 'left',
      },
      {
        binding: 'excpNm',
        header: String(t('com.label.협력사명', '협력사명')),
        width: 140,
        align: 'center',
      },
      {
        binding: 'pakItemNo',
        header: String(t('com.label.품목 No', '품목 No')),
        width: 140,
        align: 'center',
      },
      {
        binding: 'pakItemNm',
        header: String(t('com.label.품목명', '품목명')),
        width: 200,
        align: 'left',
      },
      {
        binding: 'pakItemQty',
        header: String(t('com.label.수량', '수량')),
        width: 100,
        align: 'right',
        dataType: 'Number',
      },
      {
        binding: 'qtyUnitCd',
        header: String(t('com.label.단위', '단위')),
        width: 80,
        align: 'center',
      },
      {
        binding: 'pakBoxNo',
        header: String(t('com.label.Box No', 'Box No')),
        width: 140,
        align: 'center',
      },
      {
        binding: 'fobCpltNm',
        header: String(t('com.label.FOB 완료여부', 'FOB 완료여부')),
        width: 140,
        align: 'center',
      },
      {
        binding: 'rfidTagUseYn',
        header: String(t('com.label.RFID 여부', 'RFID 여부')),
        width: 100,
        align: 'center',
      },
      {
        binding: 'ctnrSrno',
        header: String(t('com.label.박스 No', 'CNTR No')),
        width: 150,
        align: 'center',
      },
      {
        header: String(t('com.label.Site', 'Site')),
        columns: [
          {
            binding: 'shdsNm',
            header: String(t('com.label.Site 명', 'Site 명')),
            width: 150,
            align: 'center',
          },
          {
            binding: 'shdsUpdEtaDt',
            header: String(t('com.label.도착 예정일', '도착 예정일')),
            width: 150,
            align: 'center',
          },
          {
            binding: 'shdsAtaDt',
            header: String(t('com.label.실제 도착일', '실제 도착일')),
            width: 150,
            align: 'center',
          },
        ],
      },
      {
        binding: 'ctnrTpCd',
        header: String(t('com.label.CNTR Type', 'CNTR Type')),
        width: 150,
        align: 'center',
      },
      {
        header: String(t('com.label.Box Size', 'Box Size')),
        columns: [
          {
            binding: 'ctnrLen',
            header: String(t('com.label.L(mm)', 'L(mm)')),
            width: 150,
            align: 'center',
          },
          {
            binding: 'ctnrBth',
            header: String(t('com.label.W(mm)', 'W(mm)')),
            width: 150,
            align: 'center',
          },
          {
            binding: 'ctnrHigt',
            header: String(t('com.label.H(mm)', 'H(mm)')),
            width: 150,
            align: 'center',
          },
          {
            binding: 'ctnrCapa',
            header: String(t('com.label.CBM', 'CBM')),
            width: 150,
            align: 'center',
          },
        ],
      },
      {
        binding: 'dataUpdUserNm',
        header: String(t('com.label.수정자', '수정자')),
        width: 150,
        align: 'center',
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('com.label.수정일자', '수정일자')),
        width: 150,
        align: 'center',
      },
    ];
  }, [packingList]);

  const badgeList = useMemo(() => {
    if (currTab === 0) {
      return [
        { code: 'PLAN', color: CommonText.Light, text: String(t('setup.label.시작전', '시작전')) },
        { code: 'DLY', color: CommonText.Error, text: String(t('setup.label.지연', '지연')) },
        { code: 'PROG', color: CommonText.Primary, text: String(t('setup.label.진행', '진행')) },
        { code: 'CPLT', color: CommonText.Confirmed, text: String(t('setup.label.완료', '완료')) },
      ];
    } else {
      return [
        {
          code: 'CPLT',
          color: CommonText.Confirmed,
          text: String(t('setup.label.완료', '완료')),
        },
        {
          code: 'NCPT',
          color: CommonText.Error,
          text: String(t('setup.label.미완료', '미완료')),
        },
      ];
    }
  }, [currTab]);

  const prdnFilter = useMemo(() => {
    return _.sortBy(
      _.uniqBy(mapChartList, 'prdnProcCd').map((o) =>
        _.omitBy(
          {
            ...o,
            selected: mapChartList
              .filter((x) => x.prdnProcCd === o.prdnProcCd)
              .every((x) => x.selected),
          },
          _.isNull
        )
      ),
      ['prdnProcCdOrder']
    );
  }, [mapChartList]);

  const eqpFilter = useMemo(() => {
    return _.uniqBy(mapChartList, 'eqpId').map((o) =>
      _.omitBy(
        {
          ...o,
          selected: mapChartList.find((x) => x.eqpId === o.eqpId)?.selected,
        },
        _.isNull
      )
    );
  }, [mapChartList]);

  const statFilter = useMemo(() => {
    return statList.map((o) => {
      const _filter = mapChartList.filter((x) => x.progStatCd === o.cmnCd);
      return {
        ...o,
        selected: _filter.length > 0 && _filter.every((x) => x.selected),
        disabled: _filter.length === 0,
      };
    });
  }, [mapChartList]);

  const filteredRecordList = useMemo(() => recordList.filter((o) => o.selected), [recordList]);

  const filteredPackingList = useMemo(() => packingList.filter((o) => o.selected), [packingList]);

  const a11yCustomProps = (index: number) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  };

  const onChgTab = (event: any, newValue: SetStateAction<number>) => {
    setCurrTab(newValue);
  };

  const onChgFilter = (event: ChangeEvent<HTMLInputElement>, checked: boolean, gbn: string) => {
    const { value } = event.target;

    setMapChartList((prev) =>
      prev.map((o) => {
        if (o[gbn] === value) return { ...o, selected: checked };
        else return o;
      })
    );
    setTrackRecordList((prev) =>
      prev.map((o) => {
        if (o[gbn] === value) return { ...o, selected: checked };
        else return o;
      })
    );
  };

  const onClickPolygon = (data: PolygonData) => {
    if (locationState.elmPrjId && data.tgtEqpId) {
      // 현장 이미지 조회
      const _params = {
        elmPrjId: locationState.elmPrjId,
        tgtEqpId: data.tgtEqpId,
      };
      getOnSiteImgList(_params);

      setExcpList((prev) => prev.map((o) => ({ ...o, selected: data.excpId === o.excpId })));
      setPackingList((prev) => prev.map((o) => ({ ...o, selected: o.tgtEqpId === data.tgtEqpId })));
      setTrackRecordList((prev) =>
        prev.map((o) => ({ ...o, selected: data.tgtEqpId === o.tgtEqpId }))
      );
    }
  };

  const onSelectExcp = (item: SetupProjectSummary) => {
    const { excpId, selected } = item;

    setExcpList((prev) =>
      prev.map((o) =>
        o.excpId === excpId ? { ...o, selected: !o.selected } : { ...o, selected: false }
      )
    );

    if (selected) {
      setMapChartList((prev) => prev.map((o) => ({ ...o, selected: true })));
      setPackingList((prev) => prev.map((o) => ({ ...o, selected: true })));
    } else {
      setMapChartList((prev) => prev.map((o) => ({ ...o, selected: o.excpId === excpId })));
      setPackingList((prev) => prev.map((o) => ({ ...o, selected: o.excpId === excpId })));
    }
  };

  const onClickFob = (item: any) => {
    setMapChartList((prev) => prev.map((o) => ({ ...o, selected: item.tgtEqpId === o.tgtEqpId })));
    setExcpList((prev) => prev.map((o) => ({ ...o, selected: o.excpId === item.excpId })));
  };

  const onInitialized = useCallback((grid, gbn = '') => {
    if (gbn === 'FOB') {
      fobGridRef.current = grid;
    } else {
      trackGridRef.current = grid;
    }
  }, []);

  const btnExcelExport = (gbn = '') => {
    let ref = null;
    let title = '';
    let sheetName = '';

    if (gbn === 'FOB') {
      ref = fobGridRef.current;
      title = mapChart.elmPrjNm + ' ' + t('setup.label.FOB 상세', 'FOB 상세');
      sheetName = t('setup.label.FOB 상세', 'FOB 상세');
    } else {
      ref = trackGridRef.current;
      title = mapChart.elmPrjNm + ' ' + t('setup.label.실적 List', '실적 List');
      sheetName = t('setup.label.실적 List', '실적 List');
    }

    CommonUtil.exportWijmoExcelGrid(ref, title, sheetName);
  };

  // 공통코드 조회
  const init = async () => {
    setStatList((await getCommonCodeNames('PRJ_SCHD_PRCD_CD')).map((o) => ({ ...o })) || []);
  };

  // 공장 Layout > 실적 List 조회
  const getTrackRecordList = (elmPrjId: string) => {
    // 실적 List 가져오기
    findSetupPrjSummTrackRecord({ elmPrjId }).then((res) => {
      setTrackRecordList(res?.map((o) => ({ ...o, selected: true })) || []);
    });
  };

  // 설비별 현장 사진 List 조회
  const getOnSiteImgList = (param) => {
    findSetupOnSiteImage(param).then((res) => setOnSiteImgList(res?.map((o) => ({ ...o })) || []));
  };

  // FOB 상세 목록 조회
  const getPackingList = (condition: SetupExcpParam) => {
    getSetupPackingList(condition).then((res) => {
      setPackingList(res?.map((o) => ({ ...o, selected: true })) || []);
    });
  };

  // FOB > 협력업체 List 조회
  const getExcpList = (elmPrjId: string) => {
    findSetupPrjSummExcpList({ elmPrjId }).then((res) =>
      setExcpList(res.map((o) => ({ ...o, selected: false })))
    );
  };

  useEffect(() => {
    // tab 이동 시 List 데이터 조회
    if (locationState.elmPrjId) {
      if (currTab === 0) {
        getTrackRecordList(locationState.elmPrjId);
      } else {
        getPackingList({ elmPrjId: locationState.elmPrjId });
        getExcpList(locationState.elmPrjId);
      }
    }
    setMapChartList((prev) => prev.map((o) => ({ ...o, selected: true })));
  }, [currTab]);

  useEffect(() => {
    // 초기값 세팅
    init();
  }, []);

  useEffect(() => {
    // params로 넘어온 프로젝트 ID 값 세팅
    if (state?.elmPrjId) {
      setLocationState(state);
    }
  }, [location]);

  useEffect(() => {
    // params로 넘어온 탭 위치 세팅
    setCurrTab(locationState.currTab || 0);
  }, [locationState.currTab]);

  useEffect(() => {
    // MapChart 데이터 세팅
    if (locationState?.elmPrjId) {
      openLoading(true);
      Promise.all([
        findSetupMapChart(locationState.elmPrjId),
        findSetupMapChartList({ elmPrjId: locationState.elmPrjId }),
      ])
        .then(([MAP, MAP_LIST]) => {
          if (MAP) setMapChart({ ...MAP });
          setMapChartList(MAP_LIST?.map((o) => ({ ...o, selected: true })) || []);
        })
        .finally(() => openLoading(false));
    } else {
      navigate(`/setup/home/project-summary`);
    }
  }, [locationState.elmPrjId]);

  useEffect(() => {
    if (mapChartList.every((o) => o.selected)) {
      setExcpList((prev) => prev.map((o) => ({ ...o, selected: false })));
      setPackingList((prev) => prev.map((o) => ({ ...o, selected: true })));
      setTrackRecordList((prev) => prev.map((o) => ({ ...o, selected: true })));
    }
  }, [mapChartList]);

  return (
    <>
      <ContainerLayout>
        <div css={tabs.wrap}>
          <Tabs value={currTab} onChange={onChgTab} css={tabs.tab}>
            <Tab label="공장 Layout" {...a11yCustomProps(0)} />
            <Tab label="FOB" {...a11yCustomProps(1)} />
            <SubTitleGroup>
              <div
                className="info warning"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(locationState.elmPrjNm || ''),
                }}
              ></div>
            </SubTitleGroup>
          </Tabs>
        </div>
        {/* 1번 Tab */}
        <CustomTabPanel value={currTab} index={0}>
          <FactoryContainer>
            <div className="mapDetailContents">
              <SummaryItem>
                <SubTitleGroup>
                  <h3>{t('setup.label.공정', '공정')}</h3>
                </SubTitleGroup>
                <div className="scroll">
                  <TableContainer css={tb.tableCol} className="left">
                    <Table>
                      <TableBody>
                        {prdnFilter.map((item, idx) => {
                          return (
                            <TableRow key={idx}>
                              <TableCell style={{ width: '32px' }}>
                                <Checkbox
                                  id={`chk-prdnCd-${item.prdnProcCd}-${idx}`}
                                  css={st.checkbox}
                                  className="custom"
                                  value={item.prdnProcCd}
                                  checked={!!item.selected}
                                  onChange={(e, c) => onChgFilter(e, c, 'prdnProcCd')}
                                />
                              </TableCell>
                              <TableCell>
                                <label htmlFor={`chk-prdnCd-${item.prdnProcCd}-${idx}`}>
                                  {item.prdnProcNm}
                                </label>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </SummaryItem>
              <SummaryItem>
                <SubTitleGroup>
                  <h3>{t('setup.label.설비', '설비')}</h3>
                </SubTitleGroup>
                <div className="scroll">
                  <TableContainer css={tb.tableCol} className="left">
                    <Table>
                      <TableBody>
                        {eqpFilter.map((item, idx) => {
                          return (
                            <TableRow key={idx}>
                              <TableCell style={{ width: '32px' }}>
                                <Checkbox
                                  id={`chk-eqpId-${item.eqpId}-${idx}`}
                                  css={st.checkbox}
                                  className="custom"
                                  value={item.eqpId}
                                  checked={!!item.selected}
                                  onChange={(e, c) => onChgFilter(e, c, 'eqpId')}
                                />
                              </TableCell>
                              <TableCell>
                                <label htmlFor={`chk-eqpId-${item.eqpId}-${idx}`}>
                                  {item.eqpNm}
                                </label>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </SummaryItem>
              <SummaryItem>
                <SubTitleGroup>
                  <h3>{t('setup.label.진행상태', '진행상태')}</h3>
                </SubTitleGroup>
                <div className="scroll">
                  <TableContainer css={tb.tableCol} className="left">
                    <Table>
                      <TableBody>
                        {statFilter.map((item, idx) => {
                          return (
                            <TableRow key={idx}>
                              <TableCell style={{ width: '32px' }}>
                                <Checkbox
                                  id={`chk-progStat-${item.cmnCd}-${idx}`}
                                  css={st.checkbox}
                                  className="custom"
                                  value={item.cmnCd}
                                  checked={!!item.selected}
                                  disabled={!!item.disabled}
                                  onChange={(e, c) => onChgFilter(e, c, 'progStatCd')}
                                />
                              </TableCell>
                              <TableCell>
                                <label htmlFor={`chk-progStat-${item.cmnCd}-${idx}`}>
                                  {item.cmnCdNm}
                                </label>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </SummaryItem>
            </div>
            <div className="mapContents">
              <SummaryItem className="tabContents">
                {mapChartList.length > 0 && mapChart.drwAtchFileId && (
                  <MapChart
                    className="border height_2"
                    polygonData={mapChartList}
                    setPolygonData={setMapChartList}
                    imageId={mapChart.drwAtchFileId || ''}
                    imageHeight={parseInt(mapChart.imgVtcLen || '0')}
                    imageWidth={parseInt(mapChart.imgWthLen || '0')}
                    showPopup={true}
                    onClickPolygon={onClickPolygon}
                    onSiteImgList={onSiteImgList}
                    badgeList={badgeList}
                  />
                )}
              </SummaryItem>
              <SummaryItem>
                <SubTitleLayout>
                  <SubTitleGroup>
                    <h3>{t('setup.label.실적 List', '실적 List')}</h3>
                    <span className="total">
                      {t('com.label.총', '총')}
                      <span>{(filteredRecordList || []).length.toLocaleString()}</span>
                      {t('com.label.건', '건')}
                    </span>
                  </SubTitleGroup>
                  <ControlBtnGroup>
                    <Button
                      css={IconButton.button}
                      className="Exceldown"
                      onClick={() => btnExcelExport('TRACK')}
                      disableRipple
                    >
                      {t('com.button.다운로드', '다운로드')}
                    </Button>
                  </ControlBtnGroup>
                </SubTitleLayout>
                <CustomGrid
                  id="track-record-grid"
                  layoutDefinition={trackRecordLayout}
                  rowData={filteredRecordList}
                  initialized={(grid) => onInitialized(grid, 'TRACK')}
                  align={'center'}
                  isSelector={false}
                  isReadOnly={true}
                  height={190}
                />
              </SummaryItem>
            </div>
          </FactoryContainer>
        </CustomTabPanel>

        <CustomTabPanel value={currTab} index={1}>
          <FOBContainer>
            <div className="mapView">
              {mapChartList.length > 0 && mapChart.drwAtchFileId && (
                <MapChart
                  className="border height_2"
                  polygonData={mapChartList}
                  setPolygonData={setMapChartList}
                  imageId={mapChart.drwAtchFileId || ''}
                  imageHeight={parseInt(mapChart.imgVtcLen || '0')}
                  imageWidth={parseInt(mapChart.imgWthLen || '0')}
                  showPopup={true}
                  onClickPolygon={onClickPolygon}
                  badgeList={badgeList}
                  badgeKey="fobCpltCd"
                  onSiteImgList={onSiteImgList}
                />
              )}
              <div className="items">
                <ul className="height_2">
                  {excpList.map((item, idx) => {
                    return (
                      <li key={idx} className={item?.selected ? 'selected' : ''}>
                        <button type="button" onClick={() => onSelectExcp(item)}>
                          <strong>{item.excpAbbrNm}</strong>
                          <span>{`${item.arivRt}% (${item.arivCnt}/${item.allCnt})`}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="note info primary">
                {`※${t(
                  'setup.label.FOB Rate는 도착 된 Box 수량 기준임',
                  'FOB Rate는 도착 된 Box 수량 기준임'
                )}`}
              </div>
            </div>
            <SubTitleLayout>
              <SubTitleGroup>
                <h3>{t('setup.label.FOB 상세', 'FOB 상세')}</h3>
                <span className="total">
                  {t('com.label.총', '총')}
                  <span>{(filteredPackingList || []).length.toLocaleString()}</span>
                  {t('com.label.건', '건')}
                </span>
              </SubTitleGroup>
              <ControlBtnGroup>
                <Button
                  css={IconButton.button}
                  className="Exceldown"
                  onClick={() => btnExcelExport('FOB')}
                  disableRipple
                >
                  {t('com.button.다운로드', '다운로드')}
                </Button>
                <GridSettingButtons
                  gridId={'fob-grid-id'}
                  layoutDefinition={fobEqpGrLayout}
                  changeShownColumns={(result) => {
                    fobGridUiRef.current?.changeColumn?.(result);
                  }}
                />
              </ControlBtnGroup>
            </SubTitleLayout>
            <CustomGrid
              id="fob-grid-id"
              ref={fobGridUiRef}
              layoutDefinition={fobEqpGrLayout}
              rowData={filteredPackingList}
              initialized={(grid) => onInitialized(grid, 'FOB')}
              height={170}
              isSelector={false}
              isReadOnly={true}
            />
          </FOBContainer>
        </CustomTabPanel>
      </ContainerLayout>

      {isOpenScheduleChart && (
        <SetupEquipmentScheduleChartPopup
          open={isOpenScheduleChart}
          close={() => setOpenScheduleChart(false)}
          condition={{ ...scheduleChartCondition }}
        />
      )}
    </>
  );
};

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function CustomTabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      <div>{children}</div>
    </div>
  );
}

export default SetupProjectSummaryPage;
